import { IconButton } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import EclipseIcon from "@material-ui/icons/FiberManualRecordRounded";
import { makeStyles } from "@material-ui/styles";
import Container from "@saleor/components/Container";
import OrderFilterBar from "@saleor/components/OrderFilterBar";
import PageHeader from "@saleor/components/PageHeader";
import useUser from "@saleor/hooks/useUser";
import NotificationBell from "@saleor/icons/NotificationBell";
import { sectionNames } from "@saleor/intl";
import { useOrderListQuery } from "@saleor/motherOrders/queries";
import { OrderListUrlSortField, orderPath } from "@saleor/motherOrders/urls";
import { FilterPageProps, PageListProps, SortPage } from "@saleor/types";
import {
  OrderDirection,
  OrderSortField,
  SmopOrderStatusEnum,
  SmopUserTypeCode
} from "@saleor/types/globalTypes";
import { canManageFulfillment } from "@saleor/utils/handlers/userHandler";
import React from "react";
import { useIntl } from "react-intl";

import { MotherOrderList_orders_edges_node } from "../../types/MotherOrderList";
import OrderList from "../OrderList";
import UnassignedOrderListDialog from "../UnassignedOrderList/UnassignedOrderListDialog";
import {
  createFilterStructure,
  OrderFilterKeys,
  OrderListFilterOpts
} from "./filters";
import { TooltipWithArrow } from "./TooltipWithArrow";

export interface OrderListPageProps
  extends PageListProps,
    FilterPageProps<OrderFilterKeys, OrderListFilterOpts>,
    SortPage<OrderListUrlSortField> {
  orders: MotherOrderList_orders_edges_node[];
  activeTab;
  queryVariables;
  generateShippingDialog: (orderId: string) => void;
  onAddRemarks: (
    orderId: string,
    orderCode: string,
    orderOldRemarks?: string,
    orderOldRemarkId?: string
  ) => void;
  onAssignRider: (
    orderId: string,
    orderCode: string,
    mallId: string,
    isFromNotification?: boolean
  ) => void;
  onOverrideStatus: (
    orderId: string,
    orderStatus: SmopOrderStatusEnum,
    newOrderStatus: SmopOrderStatusEnum,
    courier?: string
  ) => void;
  onUpdateStatus: (
    orderId: string,
    orderStatus: SmopOrderStatusEnum,
    newOrderStatus: SmopOrderStatusEnum,
    courier?: string
  ) => void;
  onCancelStatus: (courier: string, orderId: string) => void;
  download?: any;
  onGrabStatusRefresh?: (orderId: string) => void;
  onPandaGoStatusRefresh?: (orderId: string) => void;
  refetchOrders;
  closeModal?: () => void;
  refetchUnassignedRiders?: boolean;
}

const useStyles = makeStyles(
  {
    eclipseIcon: {
      color: "#FF0000",
      left: "25px",
      margin: "0 auto",
      position: "absolute",
      textAlign: "center",
      top: "-9px",
      width: 25
    },
    iconButtonDefault: {
      "&:hover": {
        backgroundColor: "#0030FF",
        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)"
      },
      alignItems: "center",
      backgroundColor: "#0030FF",
      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
      color: "#fff",
      display: "flex",
      justifyContent: "center",
      padding: 5
    },
    iconButtonHasUnassigned: {
      backgroundColor: "primary",
      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)"
    },
    motherOrderLength: {
      color: "#0030FF",
      fontWeight: 600
    }
  },
  { name: "OrderListPage" }
);

const OrderListPage: React.FC<OrderListPageProps> = ({
  refetchOrders,
  activeTab,
  currencySymbol,
  currentTab,
  initialSearch,
  filterOpts,
  queryVariables,
  tabs,
  onAdd,
  onAll,
  onSearchChange,
  onFilterChange,
  onTabChange,
  onTabDelete,
  onTabSave,
  generateShippingDialog,
  onAssignRider,
  onAddRemarks,
  onOverrideStatus,
  onUpdateStatus,
  onCancelStatus,
  download,
  onGrabStatusRefresh,
  onPandaGoStatusRefresh,
  closeModal,
  refetchUnassignedRiders,
  ...listProps
}) => {
  const classes = useStyles(listProps);
  const intl = useIntl();
  const user = useUser();
  const isOpsAdmin = user.user?.userType.code === SmopUserTypeCode.OMI_OPS;
  const disableAction = !canManageFulfillment();
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const filterStructure = createFilterStructure(intl, filterOpts, [
    OrderFilterKeys.mall
  ]);

  const { data: unAssignedOrders, loading, refetch } = useOrderListQuery({
    displayLoader: true,
    variables: {
      filter: {
        isOrderAutobookingFailed: true
      },
      first: 100,
      sort: {
        direction: OrderDirection.DESC,
        field: OrderSortField.CREATION_DATE
      }
    }
  });

  React.useEffect(() => {
    if (refetchUnassignedRiders) {
      refetch();
    }
  }, [refetchUnassignedRiders]);

  const unfulfilledOrders =
    unAssignedOrders?.orders?.edges?.filter(
      order => order?.node?.status === "UNFULFILLED"
    ) || [];

  const hasUnfulfilledOrders = unfulfilledOrders.length === 0;

  const dialogPopperStyle = {
    width: !hasUnfulfilledOrders ? "210px" : "auto",
    zIndex: 1100
  };

  React.useEffect(() => {
    if (hasUnfulfilledOrders) {
      setIsModalOpen(false);
    }
  }, [hasUnfulfilledOrders]);

  return (
    <Container>
      <PageHeader title={intl.formatMessage(sectionNames.fulfillment)}>
        <TooltipWithArrow
          key={unfulfilledOrders.length}
          open={!isModalOpen}
          id={`fulfillment-notification-tooltip`}
          title={
            !hasUnfulfilledOrders ? (
              <>
                No riders auto-booked yet for{" "}
                <span className={classes.motherOrderLength}>
                  {unfulfilledOrders.length} Mother Order(s)
                </span>
              </>
            ) : (
              "No activity to show"
            )
          }
          placement="left"
          PopperProps={{
            style: dialogPopperStyle
          }}
        >
          <IconButton
            className={classes.iconButtonDefault}
            onClick={() => setIsModalOpen(true)}
            disabled={hasUnfulfilledOrders}
          >
            <EclipseIcon
              className={classes.eclipseIcon}
              visibility={!hasUnfulfilledOrders ? "visible" : "hidden"}
            />
            <NotificationBell size={36} />
          </IconButton>
        </TooltipWithArrow>
        <UnassignedOrderListDialog
          onAssignRider={onAssignRider}
          loading={loading}
          refetch={refetchOrders}
          unassignedOrders={
            unAssignedOrders?.orders?.edges
              ?.map(order => order.node)
              .reverse() || []
          }
          isModalOpen={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
        />
      </PageHeader>
      <Card>
        <OrderFilterBar
          currencySymbol={currencySymbol}
          currentTab={currentTab}
          initialSearch={initialSearch}
          onAll={onAll}
          onFilterChange={onFilterChange}
          onSearchChange={onSearchChange}
          onTabChange={onTabChange}
          onTabDelete={onTabDelete}
          onTabSave={onTabSave}
          tabs={tabs}
          isDownloadVisible={isOpsAdmin}
          isAllAtStart={isOpsAdmin}
          allTabLabel={intl.formatMessage({
            defaultMessage: "All",
            description: "tab name"
          })}
          filterStructure={filterStructure}
          searchPlaceholder={intl.formatMessage({
            defaultMessage: "Search Orders..."
          })}
          queryVariables={queryVariables}
          activeTab={activeTab}
          download={download}
        />
        <OrderList
          {...listProps}
          generateShippingDialog={generateShippingDialog}
          onAssignRider={onAssignRider}
          onAddRemarks={onAddRemarks}
          onOverrideStatus={onOverrideStatus}
          onUpdateStatus={onUpdateStatus}
          onCancelStatus={onCancelStatus}
          onGrabStatusRefresh={onGrabStatusRefresh}
          onPandaGoStatusRefresh={onPandaGoStatusRefresh}
          refetchOrders={refetchOrders}
          disableAction={disableAction}
          detailsPath={orderPath}
          includedColumns={[
            "Order Status",
            "Brands",
            "Bin",
            "Order Date",
            "Mother Order",
            "Delivery",
            "3PL Status",
            "Aging",
            "Payment",
            "Customer",
            "Lat, Long",
            "Rider Name",
            "Time Dispatched",
            "Time Delivered",
            "On Time",
            "Reason",
            "Ops Admin Reason for Late Delivery",
            "Remarks",
            "Print"
          ]}
        />
      </Card>
    </Container>
  );
};
OrderListPage.displayName = "OrderListPage";
export default OrderListPage;
